import { ImmutableObject } from "@hookstate/core"
import { Settings } from "@mui/icons-material"
import AddIcon from "@mui/icons-material/Add"
import DeleteIcon from "@mui/icons-material/Delete"
import RemoveIcon from "@mui/icons-material/Remove"
import { CardActions, CardContent, Collapse, IconButton } from "@mui/material"
import MatCard from "@mui/material/Card"

import { FC, useState } from "react"

import {
  AdvancedFormFieldDependency,
  AdvancedForms,
} from "src/components/common/advancedForm"
import { Button } from "src/components/common/button"
import { create } from "src/helpers/bem"

import { deepClone } from "src/helpers/deepClone"
import { calculatePrice, formatPrice } from "src/helpers/price"
import { useTranslation } from "src/hooks/translation"
import { mergeDialog } from "src/states/dialogs"
import { useLocale } from "src/states/locale"
import {
  CartProduct,
  getCartProductAmount,
  mergeCart,
} from "src/states/shop/carts"

import { mergeProductAttributes } from "src/states/shop/productAttributes"
import { ProductVariant } from "src/states/shop/productVariants"
import { xRMApiField, xRMApiFieldValueDictionary } from "src/types/xRM"

import styles from "./ShopCartProductRow.module.scss"

const bem = create(styles, "ShopCartProductRow")

export type ShopCartProductRowProps = {
  item: ImmutableObject<CartProduct>
  displayDetails?: boolean
}

export const ShopCartProductRow: FC<ShopCartProductRowProps> = ({ item }) => {
  const locale = useLocale()
  const translation = useTranslation()
  const m = translation.messages.pages.shop.cart.productCard
  const productVariant = item.productVariant!
  const [detailsExpanded, setDetailsExpanded] = useState(false)
  const [loading, setLoading] = useState(false)

  const { productCalculation, basePrice, mandatoryProducts, quantity } =
    productVariant

  const unitPrice = calculatePrice({
    calculationMethod: productCalculation,
    price: basePrice ?? 0,
    quantity: quantity ?? 1,
  })

  const total =
    unitPrice! * getCartProductAmount(item) +
    mandatoryProducts!.reduce(
      (sum, mandatory) =>
        sum + (mandatory.basePrice ?? 0) * (mandatory.quantity ?? 1),
      0,
    )

  const handleExpandClick = () => {
    setDetailsExpanded(!detailsExpanded)
  }

  const hasAttributeValue = (attribute: xRMApiFieldValueDictionary) => {
    return attribute.value !== undefined && attribute.value !== null
  }

  const modifier = {
    "is-expanded": detailsExpanded,
  }

  const hasAttributes = item.productAttributeValues?.some(
    (attribute) =>
      (attribute.referenceType === "ProductAttribute" ||
        attribute.referenceType === "PONumber" ||
        attribute.referenceType === "Pretix") &&
      attribute.value != null,
  )

  const hasMandatoryProducts =
    item.productVariant?.mandatoryProducts &&
    item.productVariant?.mandatoryProducts.length > 0

  const renderAttributeLabelAndValue = ({
    label,
    value,
  }: {
    label: any
    value: any
  }) => {
    return (
      <div className={bem("attributes")}>
        <span className={bem("attributes__label")}>{label}:</span>
        <div>{value}</div>
      </div>
    )
  }

  const getFieldValue = (field: xRMApiField, attributeValue: any) => {
    switch (field?.type) {
      case "select":
        if (Array.isArray(attributeValue)) {
          return attributeValue
            .map((v) => field.items?.find((i) => i.key === v)?.value)
            .join(", ")
        } else {
          return field.items?.find((i) => i.key === attributeValue)?.value
        }
      case "checkbox":
        return attributeValue?.toString() === "true"
          ? m.checkboxValueTrue
          : m.checkboxValueFalse
      case "file":
        return m.fileValueText
      default:
        return attributeValue?.toString()
    }
  }

  const parseAttribute = (attribute: xRMApiFieldValueDictionary) => {
    const fields = item.productVariant?.productAttributesForm?.fields
    let field = fields?.find((f) => f.field === attribute.field)

    if (attribute.referenceType === "ProductAttribute") {
      field = fields?.find((f) => f.id === attribute.field)
    }

    let label = field?.label
    if (attribute.referenceType === "Pretix") {
      label = `Pretix ${field?.label}`
    }

    const value = getFieldValue(field as xRMApiField, attribute.value)
    return renderAttributeLabelAndValue({ label, value })
  }

  const openProductDetails = () => {
    const productAttributesFormFields =
      productVariant.productAttributesForm?.fields?.map((field) => {
        const attrValue = item.productAttributeValues?.find(
          (attr) => attr.field == field.field,
        )?.value
        return { ...field, value: attrValue }
      })

    const productAttributesForm = {
      ...productVariant.productAttributesForm,
      fields: productAttributesFormFields,
    }
    mergeProductAttributes({
      open: true,
      cartProductId: item.id,
      productVariant: deepClone<ProductVariant>(productVariant),
      form: [deepClone<AdvancedForms>(productAttributesForm)],
      dependencies: deepClone<AdvancedFormFieldDependency[]>(
        productVariant.productAttributeDependencies,
      ),
    })
    mergeDialog("productAttributeDialog", { open: true })
  }

  return (
    <MatCard className={bem("card")}>
      <CardContent className={bem("contents")}>
        <div className={bem("info")}>
          <div className={bem("info__name")}>{productVariant.name}</div>
          {productCalculation === "Fix" && (
            <div className={bem("info__sum")}>
              {formatPrice(unitPrice, locale)}/ {productVariant.unit}
            </div>
          )}
        </div>
        <div className={bem("info")}>
          {productCalculation === "Fix" && (
            <div className={bem("amount")}>
              <div className={bem("counter")}>
                <div className={bem("counter__widget")}>
                  <IconButton
                    className={bem("counter__button")}
                    disabled={getCartProductAmount(item) <= 1 || loading}
                    onClick={async () => {
                      try {
                        setLoading(true)
                        await mergeCart(item, (amount) => amount - 1)
                      } finally {
                        setLoading(false)
                      }
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                  <div className={bem("counter__unit")}>
                    {`${getCartProductAmount(item)} ${productVariant.unit}`}
                  </div>
                  <IconButton
                    className={bem("counter__button")}
                    disabled={
                      getCartProductAmount(item) >=
                        (item?.productVariant?.maximumOrderAmount ?? 1000000) ||
                      loading
                    }
                    onClick={async () => {
                      try {
                        setLoading(true)
                        await mergeCart(item, (amount) => amount + 1)
                      } finally {
                        setLoading(false)
                      }
                    }}
                  >
                    <AddIcon />
                  </IconButton>
                </div>
              </div>
            </div>
          )}
          <h5 className={bem("total")}>{formatPrice(total, locale)}</h5>
        </div>
      </CardContent>

      {hasMandatoryProducts && (
        <CardContent className={bem("attributescontent")}>
          <div className={bem("attributes")}>
            <div className={bem("attributes__label")}>
              {m.mandatoryProductsLabel}
            </div>
            <div className={bem("attributes__value")}>
              {item?.productVariant?.mandatoryProducts?.map((p, index) => (
                <div key={index}>
                  {p.quantity != null
                    ? `${p.quantity * getCartProductAmount(item)} x ${
                        p.name
                      } (à ${formatPrice(p.basePrice)})`
                    : `${p.name} (à ${formatPrice(p.basePrice)})`}
                  {index <
                    (item?.productVariant?.mandatoryProducts?.length ?? 0) -
                      1 && ", "}
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      )}

      <CardActions className={bem("actions")}>
        <Button
          startIcon={<Settings />}
          title={m.expand}
          variant="text"
          onClick={() => openProductDetails()}
        />
        <Button
          startIcon={<DeleteIcon />}
          title={m.remove}
          variant="text"
          onClick={async () => await mergeCart(item, (_) => 0)}
        />
      </CardActions>
      <Collapse unmountOnExit in={true} timeout="auto"></Collapse>
    </MatCard>
  )
}
